import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createSalaryRecord,
  listSalaryRecords,
  updateSalaryRecord,
  deleteSalaryRecord,
  getSalaryStats,
  approveSalaryRecord,
  approveAllSalaryRecords,
  holdSalaryRecord,
  generateSalary,
} from '../actions/salaryActions';
import {
  uploadAdvanceExcel,
  listAdvanceRecords,
  updateAdvanceRecord,
  deleteAdvanceRecord,
  uploadAttendanceExcel,
  listAttendanceRecords,
  updateAttendanceRecord,
  deleteAttendanceRecord,
  getAttendanceStats,
  uploadBonusExcel,
  listBonusRecords,
  updateBonusRecord,
  deleteBonusRecord,
} from '../actions/masterActions';
import { listActiveCompanies } from '../actions/companyActions';
import { exportBankSheetTXT, exportBankSheetDOC, exportBankSheetExcel, exportSalaryExcel } from '../actions/exportActions';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  CheckCircle as ApproveIcon,
  PauseCircle as HoldIcon,
} from '@mui/icons-material';
import {
  SALARY_CREATE_RESET,
  SALARY_UPDATE_RESET,
  SALARY_DELETE_RESET,
  SALARY_APPROVE_RESET,
  SALARY_APPROVE_ALL_RESET,
  SALARY_HOLD_RESET,
  SALARY_GENERATE_RESET,
} from '../constants/salaryConstants';
import {
  ADVANCE_UPLOAD_RESET,
  ADVANCE_UPDATE_RESET,
  ADVANCE_DELETE_RESET,
  ATTENDANCE_UPLOAD_RESET,
  ATTENDANCE_UPDATE_RESET,
  ATTENDANCE_DELETE_RESET,
  BONUS_UPLOAD_RESET,
  BONUS_UPDATE_RESET,
  BONUS_DELETE_RESET,
} from '../constants/masterConstants';

const SalaryManagementWithMasters = () => {
  const dispatch = useDispatch();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Common state
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedCompany, setSelectedCompany] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchEmployeeCode, setSearchEmployeeCode] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCompany, setFilterCompany] = useState('');

  // Salary state
  const [generateDialog, setGenerateDialog] = useState(false);
  const [generateData, setGenerateData] = useState({ month: '', year: new Date().getFullYear(), company: '', type: '' });
  const [salaryEditDialog, setSalaryEditDialog] = useState(false);
  const [salaryAddDialog, setSalaryAddDialog] = useState(false);
  const [salaryHoldDialog, setSalaryHoldDialog] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [salaryEditData, setSalaryEditData] = useState({});
  const [newSalaryData, setNewSalaryData] = useState({
    employeeCode: '',
    firstName: '',
    middleName: '',
    lastName: '',
    jobTitle: '',
    mobile: '',
    alternateNumber: '',
    nameAsPerBank: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountType: 'SB',
    bankType: '',
    panCard: '',
    salary: '',
    attendance: '',
    advancePay: '',
    tds: '',
    supervisorName: '',
    process: '',
    type: '',
    category: '',
    company: '',
    month: '',
    year: new Date().getFullYear()
  });

  // Advance state
  const [advanceFile, setAdvanceFile] = useState(null);
  const [advanceEditDialog, setAdvanceEditDialog] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [advanceEditData, setAdvanceEditData] = useState({});

  // Attendance state
  const [attendanceFile, setAttendanceFile] = useState(null);
  const [attendanceEditDialog, setAttendanceEditDialog] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);

  // Bonus state
  const [bonusFile, setBonusFile] = useState(null);
  const [bonusEditDialog, setBonusEditDialog] = useState(false);
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [attendanceEditData, setAttendanceEditData] = useState({});

  // Export state
  const [exportDialog, setExportDialog] = useState(false);
  const [exportData, setExportData] = useState({
    month: '',
    year: new Date().getFullYear(),
    company: '',
    bankName: '',
    bankType: '',
    category: '',
    date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).replace(/ /g, '-'),
    valueDate: new Date().toLocaleDateString('en-GB').replace(/\//g, '/')
  });
  const [exportLoading, setExportLoading] = useState(false);

  // Approval state
  const [approveAllDialog, setApproveAllDialog] = useState(false);

  // Redux state - Salary
  const salaryCreate = useSelector((state) => state.salaryCreate);
  const salaryList = useSelector((state) => state.salaryList);
  const salaryUpdate = useSelector((state) => state.salaryUpdate);
  const salaryDelete = useSelector((state) => state.salaryDelete);
  const salaryStats = useSelector((state) => state.salaryStats);
  const salaryApprove = useSelector((state) => state.salaryApprove);
  const salaryApproveAll = useSelector((state) => state.salaryApproveAll);
  const salaryHold = useSelector((state) => state.salaryHold);
  const salaryGenerate = useSelector((state) => state.salaryGenerate);

  // Get user info for permission checks
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  // Redux state - Advance
  const advanceUpload = useSelector((state) => state.advanceUpload);
  const advanceList = useSelector((state) => state.advanceList);
  const advanceUpdate = useSelector((state) => state.advanceUpdate);
  const advanceDelete = useSelector((state) => state.advanceDelete);

  // Redux state - Attendance
  const attendanceUpload = useSelector((state) => state.attendanceUpload);
  const attendanceList = useSelector((state) => state.attendanceList);
  const attendanceUpdate = useSelector((state) => state.attendanceUpdate);
  const attendanceDelete = useSelector((state) => state.attendanceDelete);
  const attendanceStats = useSelector((state) => state.attendanceStats);

  // Redux state - Bonus
  const bonusUpload = useSelector((state) => state.bonusUpload);
  const bonusList = useSelector((state) => state.bonusList);
  const bonusUpdate = useSelector((state) => state.bonusUpdate);
  const bonusDelete = useSelector((state) => state.bonusDelete);

  // Redux state - Company
  const companyList = useSelector((state) => state.companyList);
  const { companies = [] } = companyList;

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Filter salary types based on user permissions
  const salaryTypes = React.useMemo(() => {
    const types = [];
    console.log('User role permissions:', {
      accessCollectionType: userInfo?.role?.accessCollectionType,
      accessVerificationType: userInfo?.role?.accessVerificationType,
      accessStandardType: userInfo?.role?.accessStandardType
    });
    if (userInfo?.role?.accessCollectionType) types.push('Collection');
    if (userInfo?.role?.accessVerificationType) types.push('Verification');
    if (userInfo?.role?.accessStandardType) types.push('Standard');
    console.log('Filtered salary types:', types);
    return types;
  }, [userInfo]);

  // Set default filter type if user has only one type permission
  useEffect(() => {
    if (salaryTypes.length === 1 && !filterType) {
      setFilterType(salaryTypes[0]);
    }
  }, [salaryTypes]);

  // Fetch companies on mount
  useEffect(() => {
    dispatch(listActiveCompanies());
  }, [dispatch]);

  // Fetch data based on active tab
  useEffect(() => {
    if (currentTabType === 'salary') {
      fetchSalaryRecords();
    } else if (currentTabType === 'advance') {
      fetchAdvanceRecords();
    } else if (currentTabType === 'attendance') {
      fetchAttendanceRecords();
    } else if (currentTabType === 'bonus') {
      fetchBonusRecords();
    }
  }, [dispatch, page, rowsPerPage, filterMonth, filterYear, searchEmployeeCode, filterType, filterCompany, tabValue]);

  // Fetch stats
  useEffect(() => {
    if (filterMonth && filterYear) {
      if (currentTabType === 'salary') {
        dispatch(getSalaryStats({ month: filterMonth, year: filterYear }));
      } else if (currentTabType === 'attendance') {
        dispatch(getAttendanceStats({ month: filterMonth, year: filterYear }));
      }
    }
  }, [dispatch, filterMonth, filterYear, tabValue]);

  // Handle generate salary success/fail
  useEffect(() => {
    if (salaryGenerate.success) {
      const r = salaryGenerate.result;
      toast.success(`Salary generated! Created/Updated: ${r.created}, Skipped (approved): ${r.skipped}, Failed: ${r.failed}`);
      setGenerateDialog(false);
      // Sync table filters to match what was generated so records appear immediately
      setFilterMonth(generateData.month);
      setFilterYear(String(generateData.year));
      setFilterType(generateData.type || '');
      setFilterCompany(generateData.company);
      setPage(0);
      dispatch({ type: SALARY_GENERATE_RESET });
    }
    if (salaryGenerate.error) {
      toast.error(salaryGenerate.error);
      dispatch({ type: SALARY_GENERATE_RESET });
    }
  }, [salaryGenerate.success, salaryGenerate.error]);

  useEffect(() => {
    if (advanceUpload.success) {
      toast.success(`Advance upload completed! Success: ${advanceUpload.uploadResult?.success}, Salary Updated: ${advanceUpload.uploadResult?.salaryUpdated}`);
      setAdvanceFile(null);
      setMonth('');
      fetchAdvanceRecords();
      dispatch({ type: ADVANCE_UPLOAD_RESET });
    }
    if (advanceUpload.error) toast.error(advanceUpload.error);
  }, [advanceUpload.success, advanceUpload.error]);

  useEffect(() => {
    if (attendanceUpload.success) {
      toast.success(`Attendance upload completed! Success: ${attendanceUpload.uploadResult?.success}, Salary Updated: ${attendanceUpload.uploadResult?.salaryUpdated}`);
      setAttendanceFile(null);
      setMonth('');
      fetchAttendanceRecords();
      dispatch({ type: ATTENDANCE_UPLOAD_RESET });
    }
    if (attendanceUpload.error) toast.error(attendanceUpload.error);
  }, [attendanceUpload.success, attendanceUpload.error]);

  // Handle create success
  useEffect(() => {
    if (salaryCreate.success) {
      toast.success('Record created successfully!');
      setSalaryAddDialog(false);
      setNewSalaryData({
        employeeCode: '',
        firstName: '',
        middleName: '',
        lastName: '',
        jobTitle: '',
        mobile: '',
        alternateNumber: '',
        nameAsPerBank: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        accountType: 'SB',
        bankType: '',
        panCard: '',
        salary: '',
        attendance: '',
        advancePay: '',
        tds: '',
        supervisorName: '',
        process: '',
        type: '',
        category: '',
        company: '',
        month: '',
        year: new Date().getFullYear()
      });
      fetchSalaryRecords();
      dispatch({ type: SALARY_CREATE_RESET });
    }
    if (salaryCreate.error) toast.error(salaryCreate.error);
  }, [salaryCreate.success, salaryCreate.error]);

  // Handle update success
  useEffect(() => {
    if (salaryUpdate.success) {
      toast.success('Salary updated!');
      setSalaryEditDialog(false);
      fetchSalaryRecords();
      dispatch({ type: SALARY_UPDATE_RESET });
    }
  }, [salaryUpdate.success]);

  useEffect(() => {
    if (advanceUpdate.success) {
      toast.success('Advance updated!');
      setAdvanceEditDialog(false);
      fetchAdvanceRecords();
      dispatch({ type: ADVANCE_UPDATE_RESET });
    }
  }, [advanceUpdate.success]);

  useEffect(() => {
    if (attendanceUpdate.success) {
      toast.success('Attendance updated!');
      setAttendanceEditDialog(false);
      fetchAttendanceRecords();
      dispatch({ type: ATTENDANCE_UPDATE_RESET });
    }
  }, [attendanceUpdate.success]);

  // Handle delete success
  useEffect(() => {
    if (salaryDelete.success) {
      toast.success('Record deleted!');
      fetchSalaryRecords();
      dispatch({ type: SALARY_DELETE_RESET });
    }
  }, [salaryDelete.success]);

  useEffect(() => {
    if (advanceDelete.success) {
      toast.success('Advance deleted!');
      fetchAdvanceRecords();
      dispatch({ type: ADVANCE_DELETE_RESET });
    }
  }, [advanceDelete.success]);

  useEffect(() => {
    if (attendanceDelete.success) {
      toast.success('Attendance deleted!');
      fetchAttendanceRecords();
      dispatch({ type: ATTENDANCE_DELETE_RESET });
    }
  }, [attendanceDelete.success]);

  // Handle approval success
  useEffect(() => {
    if (salaryApprove.success) {
      toast.success('Salary record approved successfully!');
      fetchSalaryRecords();
      if (filterMonth && filterYear) {
        dispatch(getSalaryStats({ month: filterMonth, year: filterYear }));
      }
      dispatch({ type: SALARY_APPROVE_RESET });
    }
    if (salaryApprove.error) {
      toast.error(salaryApprove.error);
      dispatch({ type: SALARY_APPROVE_RESET });
    }
  }, [salaryApprove.success, salaryApprove.error]);

  useEffect(() => {
    if (salaryApproveAll.success) {
      toast.success(salaryApproveAll.result?.message || 'All salary records approved successfully!');
      setApproveAllDialog(false);
      fetchSalaryRecords();
      if (filterMonth && filterYear) {
        dispatch(getSalaryStats({ month: filterMonth, year: filterYear }));
      }
      dispatch({ type: SALARY_APPROVE_ALL_RESET });
    }
    if (salaryApproveAll.error) {
      toast.error(salaryApproveAll.error);
      dispatch({ type: SALARY_APPROVE_ALL_RESET });
    }
  }, [salaryApproveAll.success, salaryApproveAll.error]);

  //Handle hold success
  useEffect(() => {
    if (salaryHold.success) {
      toast.success('Salary record put on hold successfully!');
      setSalaryHoldDialog(false);
      setHoldReason('');
      fetchSalaryRecords();
      dispatch({ type: SALARY_HOLD_RESET });
    }
    if (salaryHold.error) {
      toast.error(salaryHold.error);
      dispatch({ type: SALARY_HOLD_RESET });
    }
  }, [salaryHold.success, salaryHold.error]);

  const fetchSalaryRecords = () => {
    const filters = { page: page + 1, limit: rowsPerPage };
    if (filterMonth) filters.month = filterMonth;
    if (filterYear) filters.year = filterYear;
    if (searchEmployeeCode) filters.employeeCode = searchEmployeeCode;
    if (filterType) filters.type = filterType;
    if (filterCompany) filters.company = filterCompany;
    dispatch(listSalaryRecords(filters));
  };

  const fetchAdvanceRecords = () => {
    const filters = { page: page + 1, limit: rowsPerPage };
    if (filterMonth) filters.month = filterMonth;
    if (filterYear) filters.year = filterYear;
    if (searchEmployeeCode) filters.employeeCode = searchEmployeeCode;
    dispatch(listAdvanceRecords(filters));
  };

  const fetchAttendanceRecords = () => {
    const filters = { page: page + 1, limit: rowsPerPage };
    if (filterMonth) filters.month = filterMonth;
    if (filterYear) filters.year = filterYear;
    if (searchEmployeeCode) filters.employeeCode = searchEmployeeCode;
    dispatch(listAttendanceRecords(filters));
  };

  const fetchBonusRecords = () => {
    const filters = { page: page + 1, limit: rowsPerPage };
    if (filterMonth) filters.month = filterMonth;
    if (filterYear) filters.year = filterYear;
    if (searchEmployeeCode) filters.employeeCode = searchEmployeeCode;
    dispatch(listBonusRecords(filters));
  };

  const handleFileChange = (e, type) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (['xlsx', 'xls', 'csv'].includes(fileExtension)) {
        if (type === 'advance') setAdvanceFile(selectedFile);
        else if (type === 'attendance') setAttendanceFile(selectedFile);
        else if (type === 'bonus') setBonusFile(selectedFile);
      } else {
        toast.error('Please select a valid Excel file');
        e.target.value = null;
      }
    }
  };

  const handleUpload = (type) => {
    if (!month || !year) {
      toast.error('Please select month and year');
      return;
    }

    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }

    const formData = new FormData();
    formData.append('month', month);
    formData.append('year', year);
    formData.append('company', selectedCompany);

    if (type === 'advance' && advanceFile) {
      formData.append('file', advanceFile);
      dispatch(uploadAdvanceExcel(formData));
    } else if (type === 'attendance' && attendanceFile) {
      formData.append('file', attendanceFile);
      dispatch(uploadAttendanceExcel(formData));
    } else if (type === 'bonus' && bonusFile) {
      formData.append('file', bonusFile);
      dispatch(uploadBonusExcel(formData));
    } else {
      toast.error('Please select a file');
    }
  };

  const handleGenerateSalary = () => {
    if (!generateData.month || !generateData.year || !generateData.company) {
      toast.error('Please select month, year, and company');
      return;
    }
    dispatch(generateSalary(generateData));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleNewSalaryChange = (e) => {
    const { name, value } = e.target;
    setNewSalaryData({ ...newSalaryData, [name]: value });
  };

  const handleSalaryAddSubmit = () => {
    // Validate required fields
    if (!newSalaryData.employeeCode || !newSalaryData.firstName || !newSalaryData.lastName ||
      !newSalaryData.month || !newSalaryData.year || !newSalaryData.company) {
      toast.error('Please fill all required fields');
      return;
    }

    dispatch(createSalaryRecord(newSalaryData));
  };

  const handleExport = async (format) => {
    if (!exportData.month || !exportData.year || !exportData.company) {
      toast.error('Please select month, year, and company');
      return;
    }
    if (format === 'doc' && !exportData.date) {
      toast.error('Please enter date for DOC export');
      return;
    }

    setExportLoading(true);
    try {
      if (format === 'txt') {
        await dispatch(exportBankSheetTXT(exportData));
        toast.success('TXT file downloaded successfully');
      } else if (format === 'excel') {
        const selectedCompanyObj = companies.find(c => c._id === exportData.company);
        const customDetails1 = selectedCompanyObj?.shortName || '';
        await dispatch(exportBankSheetExcel({ ...exportData, date: exportData.valueDate, customDetails1 }));
        toast.success('Excel file downloaded successfully');
      } else {
        await dispatch(exportBankSheetDOC(exportData));
        toast.success('DOC file downloaded successfully');
      }
      setExportDialog(false);
    } catch (error) {
      toast.error(error.message || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleApproveSalary = (id) => {
    if (window.confirm('Are you sure you want to approve this salary record?')) {
      dispatch(approveSalaryRecord(id));
    }
  };

  const handleApproveAllClick = () => {
    if (!filterMonth || !filterYear) {
      toast.error('Please select month and year filters to approve all records');
      return;
    }
    setApproveAllDialog(true);
  };

  const handleApproveAllConfirm = () => {
    const approvalData = {
      month: filterMonth,
      year: parseInt(filterYear),
      company: selectedCompany || undefined
    };
    dispatch(approveAllSalaryRecords(approvalData));
  };

  const handleHoldSalary = (record) => {
    setSelectedSalary(record);
    setHoldReason('');
    setSalaryHoldDialog(true);
  };

  const handleHoldConfirm = () => {
    if (!holdReason.trim()) {
      toast.error('Please provide a reason for holding this salary record');
      return;
    }
    dispatch(holdSalaryRecord(selectedSalary._id, holdReason));
  };

  // Check if user has approval permission
  const hasApprovalPermission = userInfo && userInfo.role && userInfo.role.salaryApproval === true;

  // Check if user is Super Admin
  const isSuperAdmin = userInfo && userInfo.role && userInfo.role.name === 'Super Admin';

  // Check role-based tab permissions
  const hasSalaryPermission = userInfo && userInfo.role && userInfo.role.salaryView === true;
  const hasAdvancePermission = userInfo && userInfo.role && userInfo.role.advanceView === true;
  const hasAttendancePermission = userInfo && userInfo.role && userInfo.role.attendanceView === true;
  const hasBonusPermission = userInfo && userInfo.role && userInfo.role.bonusMaster === true;

  // Build visible tabs array with type mapping
  const visibleTabs = [];
  if (hasSalaryPermission) visibleTabs.push({ label: 'Salary Master', type: 'salary' });
  if (hasAdvancePermission) visibleTabs.push({ label: 'Advance Master', type: 'advance' });
  if (hasAttendancePermission) visibleTabs.push({ label: 'Attendance Master', type: 'attendance' });
  if (hasBonusPermission) visibleTabs.push({ label: 'Bonus Master', type: 'bonus' });

  // Get current tab type based on tabValue
  const currentTabType = visibleTabs[tabValue]?.type || 'salary';

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Salary Management System
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          {hasSalaryPermission && <Tab label="Salary Master" />}
          {hasAdvancePermission && <Tab label="Advance Master" />}
          {hasAttendancePermission && <Tab label="Attendance Master" />}
          {hasBonusPermission && <Tab label="Bonus Master" />}
        </Tabs>
      </Paper>

      {/* Upload / Action Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {currentTabType === 'salary' && 'Salary Master'}
            {currentTabType === 'advance' && 'Upload Advance Data'}
            {currentTabType === 'attendance' && 'Upload Attendance Data'}
            {currentTabType === 'bonus' && 'Upload Bonus Data'}
          </Typography>
          {currentTabType === 'salary' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={() => setGenerateDialog(true)}
              >
                Generate Salary
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => setSalaryAddDialog(true)}
              >
                Add New Entry
              </Button>
            </Box>
          )}
        </Box>

        {/* Non-salary tabs: show upload controls */}
        {currentTabType !== 'salary' && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>
                <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Month">
                  {months.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} label="Company">
                  {companies && companies.length > 0 ? (
                    companies.map((company) => (
                      <MenuItem key={company._id} value={company._id}>
                        {company.companyName}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No companies available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="outlined" component="label" fullWidth startIcon={<UploadIcon />}>
                {(currentTabType === 'advance' && advanceFile) ? advanceFile.name :
                  (currentTabType === 'attendance' && attendanceFile) ? attendanceFile.name :
                    (currentTabType === 'bonus' && bonusFile) ? bonusFile.name :
                      'Choose Excel File'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => handleFileChange(e, currentTabType)}
                />
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => handleUpload(currentTabType)}
                disabled={
                  (currentTabType === 'advance' && (advanceUpload.loading || !advanceFile)) ||
                  (currentTabType === 'attendance' && (attendanceUpload.loading || !attendanceFile)) ||
                  (currentTabType === 'bonus' && (bonusUpload.loading || !bonusFile))
                }
                startIcon={<UploadIcon />}
              >
                Upload
              </Button>
            </Grid>
          </Grid>
        )}
        {((currentTabType === 'advance' && advanceUpload.loading) || (currentTabType === 'attendance' && attendanceUpload.loading) || (currentTabType === 'bonus' && bonusUpload.loading)) && <LinearProgress sx={{ mt: 2 }} />}
      </Paper>

      {/* Statistics */}
      {((currentTabType === 'salary' && salaryStats.stats && Object.keys(salaryStats.stats).length > 0) ||
        (currentTabType === 'attendance' && attendanceStats.stats && Object.keys(attendanceStats.stats).length > 0)) && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {currentTabType === 'salary' && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="textSecondary">Total Employees</Typography>
                    <Typography variant="h5">{salaryStats.stats.totalEmployees || 0}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="textSecondary">Total Salary</Typography>
                    <Typography variant="h5">{formatCurrency(salaryStats.stats.totalSalary)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="textSecondary">Total Payable Salary</Typography>
                    <Typography variant="h5">{formatCurrency(salaryStats.stats.totalNetSalary)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="textSecondary">Avg Attendance</Typography>
                    <Typography variant="h5">{salaryStats.stats.avgAttendance ? salaryStats.stats.avgAttendance.toFixed(1) : 0} days</Typography>
                  </CardContent></Card>
                </Grid>
              </>
            )}
            {currentTabType === 'attendance' && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="textSecondary">Total Employees</Typography>
                    <Typography variant="h5">{attendanceStats.stats.totalEmployees || 0}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="textSecondary">Avg Attendance</Typography>
                    <Typography variant="h5">{attendanceStats.stats.avgAttendance?.toFixed(1) || 0} days</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="textSecondary">Total Present</Typography>
                    <Typography variant="h5">{attendanceStats.stats.totalPresentDays || 0}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="textSecondary">Total Absent</Typography>
                    <Typography variant="h5">{attendanceStats.stats.totalAbsentDays || 0}</Typography>
                  </CardContent></Card>
                </Grid>
              </>
            )}
          </Grid>
        )}

      {/* Export and Approval Buttons for Salary Tab */}
      {currentTabType === 'salary' && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {hasApprovalPermission && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ApproveIcon />}
              onClick={handleApproveAllClick}
              disabled={salaryApproveAll.loading || !filterMonth || !filterYear}
            >
              {salaryApproveAll.loading ? 'Approving...' : 'Approve All'}
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={async () => {
              try {
                const filters = {};
                if (filterMonth) filters.month = filterMonth;
                if (filterYear) filters.year = filterYear;
                if (filterCompany) filters.company = filterCompany;
                if (filterType) filters.type = filterType;
                await dispatch(exportSalaryExcel(filters));
                toast.success('Excel file downloaded successfully');
              } catch (error) {
                toast.error(error.message || 'Export failed');
              }
            }}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialog(true)}
          >
            Export Bank Sheet
          </Button>
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Employee Code"
              value={searchEmployeeCode}
              onChange={(e) => { setSearchEmployeeCode(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter Month</InputLabel>
              <Select
                value={filterMonth}
                onChange={(e) => { setFilterMonth(e.target.value); setPage(0); }}
                label="Filter Month"
              >
                <MenuItem value="">All</MenuItem>
                {months.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Filter Year"
              value={filterYear}
              onChange={(e) => { setFilterYear(e.target.value); setPage(0); }}
            />
          </Grid>
          {currentTabType === 'salary' && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Filter Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
                  label="Filter Type"
                >
                  <MenuItem value="">All</MenuItem>
                  {salaryTypes.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {currentTabType === 'salary' && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Filter Company</InputLabel>
                <Select
                  value={filterCompany}
                  onChange={(e) => { setFilterCompany(e.target.value); setPage(0); }}
                  label="Filter Company"
                >
                  <MenuItem value="">All</MenuItem>
                  {companies && companies.map((comp) => (
                    <MenuItem key={comp._id} value={comp._id}>
                      {comp.companyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={() => {
                if (currentTabType === 'salary') fetchSalaryRecords();
                else if (currentTabType === 'advance') fetchAdvanceRecords();
                else if (currentTabType === 'attendance') fetchAttendanceRecords();
                else if (currentTabType === 'bonus') fetchBonusRecords();
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tables */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {currentTabType === 'salary' && (
                  <>
                    <TableCell>Employee Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Job Title</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Supervisor Name</TableCell>
                    <TableCell>Process</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="center">Category</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Alt Mobile</TableCell>
                    <TableCell>Name as per Bank</TableCell>
                    <TableCell>Bank Name</TableCell>
                    <TableCell>Account Number</TableCell>
                    <TableCell>IFSC Code</TableCell>
                    <TableCell>A/C Type</TableCell>
                    <TableCell>Bank Type</TableCell>
                    <TableCell>PAN Card</TableCell>
                    <TableCell align="right">Salary</TableCell>
                    <TableCell align="center">Attendance</TableCell>
                    <TableCell align="right">Bonus</TableCell>
                    <TableCell align="right">Net Salary</TableCell>
                    <TableCell align="center">TDS %</TableCell>
                    <TableCell align="right">TDS Amount</TableCell>
                    <TableCell align="right">Advance Pay</TableCell>
                    <TableCell align="right">Payable Salary</TableCell>
                    <TableCell>Month/Year</TableCell>
                    <TableCell align="center">Approval Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </>
                )}
                {currentTabType === 'advance' && (
                  <>
                    <TableCell>Employee Code</TableCell>
                    <TableCell>Employee Name</TableCell>
                    <TableCell align="right">Total Advance</TableCell>
                    <TableCell align="right">Deducted Amount</TableCell>
                    <TableCell align="right">Remaining Balance</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Month/Year</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </>
                )}
                {currentTabType === 'attendance' && (
                  <>
                    <TableCell>Employee Code</TableCell>
                    <TableCell>Employee Name</TableCell>
                    <TableCell align="center">Attendance</TableCell>
                    <TableCell align="center">Present Days</TableCell>
                    <TableCell align="center">Absent Days</TableCell>
                    <TableCell align="center">Leave Days</TableCell>
                    <TableCell>Tag</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Month/Year</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </>
                )}
                {currentTabType === 'bonus' && (
                  <>
                    <TableCell>Employee Code</TableCell>
                    <TableCell>Employee Name</TableCell>
                    <TableCell align="right">Bonus Amount</TableCell>
                    <TableCell>Bonus Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Month/Year</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {(currentTabType === 'salary' && salaryList.loading) ||
                (currentTabType === 'advance' && advanceList.loading) ||
                (currentTabType === 'attendance' && attendanceList.loading) ||
                (currentTabType === 'bonus' && bonusList.loading) ? (
                <TableRow>
                  <TableCell colSpan={10} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : (
                <>
                  {currentTabType === 'salary' && salaryList.records && salaryList.records.map((record) => (
                    <TableRow key={record._id}>
                      <TableCell>{record.employeeCode}</TableCell>
                      <TableCell>{`${record.firstName} ${record.middleName || ''} ${record.lastName}`.trim()}</TableCell>
                      <TableCell>{record.jobTitle}</TableCell>
                      <TableCell>{record.company?.companyName || '-'}</TableCell>
                      <TableCell>{record.supervisorName || '-'}</TableCell>
                      <TableCell>{record.process || '-'}</TableCell>
                      <TableCell>
                        {record.type ? (
                          <Chip label={record.type} size="small" color="primary" variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {record.category ? (
                          <Chip label={record.category} size="small" color="info" variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell>{record.mobile || '-'}</TableCell>
                      <TableCell>{record.alternateNumber || '-'}</TableCell>
                      <TableCell>{record.nameAsPerBank || '-'}</TableCell>
                      <TableCell>{record.bankName || '-'}</TableCell>
                      <TableCell>{record.accountNumber || '-'}</TableCell>
                      <TableCell>{record.ifscCode || '-'}</TableCell>
                      <TableCell>
                        {record.accountType ? (
                          <Chip
                            label={record.accountType}
                            size="small"
                            color={record.accountType === 'SB' ? 'success' : 'primary'}
                            variant="outlined"
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {record.bankType ? (
                          <Chip
                            label={record.bankType}
                            size="small"
                            color={record.bankType === 'Bob' ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>{record.panCard || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(record.salary)}</TableCell>
                      <TableCell align="center">
                        <Chip label={record.attendance} size="small" color={record.attendance >= 25 ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell align="right">
                        <strong style={{ color: '#2e7d32' }}>{formatCurrency(record.bonus || 0)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(record.payableSalary)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        {record.tds ? (
                          <Chip label={`${record.tds}%`} size="small" color="info" variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(record.tdsAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(record.advancePay)}</TableCell>
                      <TableCell align="right"><strong style={{ color: '#2e7d32' }}>{formatCurrency(record.netSalary)}</strong></TableCell>
                      <TableCell>{record.month} {record.year}</TableCell>
                      <TableCell align="center">
                        {record.isApproved ? (
                          <Chip
                            label={'Approved'}
                            size="small"
                            color="success"
                            icon={<ApproveIcon />}
                          />
                        ) : record.isOnHold ? (
                          <Chip
                            label={'On Hold'}
                            size="small"
                            color="error"
                            icon={<HoldIcon />}
                          />
                        ) : (
                          <Chip label="Pending" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                          {hasApprovalPermission && !record.isApproved && (
                            <>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApproveSalary(record._id)}
                                disabled={salaryApprove.loading}
                                title={record.isOnHold ? "Approve (will remove hold)" : "Approve"}
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                              {!record.isOnHold && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleHoldSalary(record)}
                                  disabled={salaryHold.loading}
                                  title="Hold"
                                >
                                  <HoldIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          )}
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedSalary(record);
                              setSalaryEditData({
                                firstName: record.firstName,
                                middleName: record.middleName,
                                lastName: record.lastName,
                                jobTitle: record.jobTitle,
                                mobile: record.mobile,
                                alternateNumber: record.alternateNumber,
                                nameAsPerBank: record.nameAsPerBank,
                                bankName: record.bankName,
                                accountNumber: record.accountNumber,
                                ifscCode: record.ifscCode,
                                accountType: record.accountType,
                                bankType: record.bankType,
                                panCard: record.panCard,
                                salary: record.salary,
                                attendance: record.attendance,
                                advancePay: record.advancePay,
                                bonus: record.bonus,
                                tds: record.tds,
                                supervisorName: record.supervisorName,
                                process: record.process,
                                type: record.type,
                                category: record.category
                              });
                              setSalaryEditDialog(true);
                            }}
                            disabled={record.isApproved && !isSuperAdmin}
                            title={record.isApproved && !isSuperAdmin ? 'Cannot edit approved records' : 'Edit'}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              if (window.confirm('Delete this record?')) dispatch(deleteSalaryRecord(record._id));
                            }}
                            disabled={record.isApproved && !isSuperAdmin}
                            title={record.isApproved && !isSuperAdmin ? 'Cannot delete approved records' : 'Delete'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}

                  {currentTabType === 'advance' && advanceList.records && advanceList.records.map((record) => (
                    <TableRow key={record._id}>
                      <TableCell>{record.employeeCode}</TableCell>
                      <TableCell>{record.employeeName}</TableCell>
                      <TableCell align="right"><strong>{formatCurrency(record.advanceAmount)}</strong></TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatCurrency(record.deductedAmount || 0)}
                          size="small"
                          color="warning"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <strong style={{ color: record.remainingAmount > 0 ? '#d32f2f' : '#2e7d32' }}>
                          {formatCurrency(record.remainingAmount || 0)}
                        </strong>
                      </TableCell>
                      <TableCell>{record.reason}</TableCell>
                      <TableCell>{record.approvedBy}</TableCell>
                      <TableCell>{record.month} {record.year}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => {
                          setSelectedAdvance(record);
                          setAdvanceEditData({ advanceAmount: record.advanceAmount, reason: record.reason });
                          setAdvanceEditDialog(true);
                        }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => {
                          if (window.confirm('Delete this advance record?')) dispatch(deleteAdvanceRecord(record._id));
                        }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {currentTabType === 'attendance' && attendanceList.records && attendanceList.records.map((record) => (
                    <TableRow key={record._id}>
                      <TableCell>{record.employeeCode}</TableCell>
                      <TableCell>{record.employeeName}</TableCell>
                      <TableCell align="center">
                        <Chip label={record.attendance} size="small" color={record.attendance >= 25 ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell align="center">{record.presentDays || 0}</TableCell>
                      <TableCell align="center">{record.absentDays || 0}</TableCell>
                      <TableCell align="center">{record.leaveDays || 0}</TableCell>
                      <TableCell>
                        <Chip label={record.tag || 'N/A'} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>{record.remarks}</TableCell>
                      <TableCell>{record.month} {record.year}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => {
                          setSelectedAttendance(record);
                          setAttendanceEditData({ attendance: record.attendance, presentDays: record.presentDays, absentDays: record.absentDays });
                          setAttendanceEditDialog(true);
                        }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => {
                          if (window.confirm('Delete this attendance record?')) dispatch(deleteAttendanceRecord(record._id));
                        }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {currentTabType === 'bonus' && bonusList.records && bonusList.records.map((record) => (
                    <TableRow key={record._id}>
                      <TableCell>{record.employeeCode}</TableCell>
                      <TableCell>{record.employeeName}</TableCell>
                      <TableCell align="right">
                        <strong style={{ color: '#2e7d32' }}>{formatCurrency(record.bonusAmount)}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip label={record.bonusType} size="small" color="success" variant="outlined" />
                      </TableCell>
                      <TableCell>{record.description || '-'}</TableCell>
                      <TableCell>{record.approvedBy || '-'}</TableCell>
                      <TableCell>{record.month} {record.year}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => {
                          setSelectedBonus(record);
                          setBonusEditDialog(true);
                        }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => {
                          if (window.confirm('Delete this bonus record?')) dispatch(deleteBonusRecord(record._id));
                        }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {((currentTabType === 'salary' && (!salaryList.records || salaryList.records.length === 0)) ||
                    (currentTabType === 'advance' && (!advanceList.records || advanceList.records.length === 0)) ||
                    (currentTabType === 'attendance' && (!attendanceList.records || attendanceList.records.length === 0)) ||
                    (currentTabType === 'bonus' && (!bonusList.records || bonusList.records.length === 0))) && (
                      <TableRow>
                        <TableCell colSpan={10} align="center">No records found</TableCell>
                      </TableRow>
                    )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={
            currentTabType === 'salary' ? (salaryList.totalRecords || 0) :
              currentTabType === 'advance' ? (advanceList.totalRecords || 0) :
                currentTabType === 'attendance' ? (attendanceList.totalRecords || 0) :
                  (bonusList.totalRecords || 0)
          }
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Edit Dialogs */}
      <Dialog open={salaryEditDialog} onClose={() => setSalaryEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Salary Record</DialogTitle>
        <DialogContent>
          {selectedSalary && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Employee Code: {selectedSalary.employeeCode}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="First Name" value={salaryEditData.firstName || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, firstName: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Middle Name" value={salaryEditData.middleName || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, middleName: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Last Name" value={salaryEditData.lastName || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, lastName: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Job Title" value={salaryEditData.jobTitle || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, jobTitle: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Mobile" value={salaryEditData.mobile || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, mobile: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Alternate Number" value={salaryEditData.alternateNumber || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, alternateNumber: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Name as per Bank" value={salaryEditData.nameAsPerBank || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, nameAsPerBank: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Bank Name" value={salaryEditData.bankName || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, bankName: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Account Number" value={salaryEditData.accountNumber || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, accountNumber: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="IFSC Code" value={salaryEditData.ifscCode || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, ifscCode: e.target.value.toUpperCase() })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Account Type</InputLabel>
                    <Select value={salaryEditData.accountType || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, accountType: e.target.value })} label="Account Type">
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="SB">SB - Savings Bank</MenuItem>
                      <MenuItem value="CB">CB - Current Bank</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Bank Type</InputLabel>
                    <Select value={salaryEditData.bankType || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, bankType: e.target.value })} label="Bank Type">
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Bob">Bob</MenuItem>
                      <MenuItem value="Non Bob">Non Bob</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PAN Card"
                    value={salaryEditData.panCard || ''}
                    onChange={(e) => setSalaryEditData({ ...salaryEditData, panCard: e.target.value.toUpperCase() })}
                    inputProps={{ maxLength: 10 }}
                    helperText="10 characters (e.g., ABCDE1234F)"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Salary" value={salaryEditData.salary || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, salary: parseFloat(e.target.value) || 0 })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Attendance" value={salaryEditData.attendance || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, attendance: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, max: 31 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Advance Pay" value={salaryEditData.advancePay || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, advancePay: parseFloat(e.target.value) || 0 })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Bonus" value={salaryEditData.bonus || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, bonus: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0 }} helperText="Manual bonus override" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="TDS (%)" value={salaryEditData.tds || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, tds: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, max: 100, step: 0.1 }} helperText="Tax Deducted at Source %" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Supervisor Name" value={salaryEditData.supervisorName || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, supervisorName: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Process" value={salaryEditData.process || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, process: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select value={salaryEditData.type || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, type: e.target.value })} label="Type">
                      <MenuItem value="">None</MenuItem>
                      {salaryTypes.map((t) => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select value={salaryEditData.category || ''} onChange={(e) => setSalaryEditData({ ...salaryEditData, category: e.target.value })} label="Category">
                      <MenuItem value="">None</MenuItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <MenuItem key={num} value={num}>{num}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => dispatch(updateSalaryRecord(selectedSalary._id, salaryEditData))} disabled={salaryUpdate.loading}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={advanceEditDialog} onClose={() => setAdvanceEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Advance Record</DialogTitle>
        <DialogContent>
          {selectedAdvance && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2">Employee: {selectedAdvance.employeeName} ({selectedAdvance.employeeCode})</Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField fullWidth type="number" label="Advance Amount" value={advanceEditData.advanceAmount || ''} onChange={(e) => setAdvanceEditData({ ...advanceEditData, advanceAmount: parseFloat(e.target.value) || 0 })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Reason" value={advanceEditData.reason || ''} onChange={(e) => setAdvanceEditData({ ...advanceEditData, reason: e.target.value })} />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvanceEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => dispatch(updateAdvanceRecord(selectedAdvance._id, advanceEditData))} disabled={advanceUpdate.loading}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={attendanceEditDialog} onClose={() => setAttendanceEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Attendance Record</DialogTitle>
        <DialogContent>
          {selectedAttendance && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2">Employee: {selectedAttendance.employeeName} ({selectedAttendance.employeeCode})</Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField fullWidth type="number" label="Attendance" value={attendanceEditData.attendance || ''} onChange={(e) => setAttendanceEditData({ ...attendanceEditData, attendance: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, max: 31 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth type="number" label="Present Days" value={attendanceEditData.presentDays || ''} onChange={(e) => setAttendanceEditData({ ...attendanceEditData, presentDays: parseFloat(e.target.value) || 0 })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth type="number" label="Absent Days" value={attendanceEditData.absentDays || ''} onChange={(e) => setAttendanceEditData({ ...attendanceEditData, absentDays: parseFloat(e.target.value) || 0 })} />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttendanceEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => dispatch(updateAttendanceRecord(selectedAttendance._id, attendanceEditData))} disabled={attendanceUpdate.loading}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Approve All Confirmation Dialog */}
      <Dialog open={approveAllDialog} onClose={() => setApproveAllDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve All Salary Records</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              You are about to approve all pending salary records for:
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Month:</strong> {filterMonth}</Typography>
              <Typography variant="body2"><strong>Year:</strong> {filterYear}</Typography>
              {selectedCompany && companies && (
                <Typography variant="body2">
                  <strong>Company:</strong> {companies.find(c => c._id === selectedCompany)?.companyName || 'Selected Company'}
                </Typography>
              )}
              {salaryStats.stats && (
                <>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Total Employees:</strong> {salaryStats.stats.totalEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="primary">
                    <strong>Total Payable Salary:</strong> {formatCurrency(salaryStats.stats.totalPayableSalary || salaryStats.stats.totalNetSalary)}
                  </Typography>
                </>
              )}
            </Box>
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone. Are you sure you want to proceed?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveAllDialog(false)} disabled={salaryApproveAll.loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApproveAllConfirm}
            disabled={salaryApproveAll.loading}
            startIcon={salaryApproveAll.loading ? <CircularProgress size={20} /> : <ApproveIcon />}
          >
            {salaryApproveAll.loading ? 'Approving...' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Salary Dialog */}
      <Dialog open={generateDialog} onClose={() => setGenerateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Salary from Employee Master</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will create salary records for all active employees using data from Employee Master, Attendance Master, Advance Master, and Bonus Master.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Month *</InputLabel>
                <Select value={generateData.month} onChange={(e) => setGenerateData({ ...generateData, month: e.target.value })} label="Month *">
                  {months.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Year *" type="number"
                value={generateData.year}
                onChange={(e) => setGenerateData({ ...generateData, year: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Company *</InputLabel>
                <Select value={generateData.company} onChange={(e) => setGenerateData({ ...generateData, company: e.target.value })} label="Company *">
                  {companies && companies.map((c) => <MenuItem key={c._id} value={c._id}>{c.companyName}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type (optional)</InputLabel>
                <Select value={generateData.type} onChange={(e) => setGenerateData({ ...generateData, type: e.target.value })} label="Type (optional)">
                  <MenuItem value="">All Types</MenuItem>
                  {salaryTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialog(false)} disabled={salaryGenerate.loading}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateSalary}
            disabled={salaryGenerate.loading}
            startIcon={salaryGenerate.loading ? <CircularProgress size={18} /> : <RefreshIcon />}
          >
            {salaryGenerate.loading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Bank Sheet Dialog */}
      <Dialog open={exportDialog} onClose={() => setExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Bank Sheet</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>
                <Select
                  value={exportData.month}
                  onChange={(e) => setExportData({ ...exportData, month: e.target.value })}
                  label="Month"
                >
                  {months.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Year"
                value={exportData.year}
                onChange={(e) => setExportData({ ...exportData, year: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select
                  value={exportData.company}
                  onChange={(e) => setExportData({ ...exportData, company: e.target.value })}
                  label="Company"
                >
                  {companies && companies.length > 0 ? (
                    companies.map((company) => (
                      <MenuItem key={company._id} value={company._id}>
                        {company.companyName}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No companies available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Name (Optional Filter)"
                value={exportData.bankName}
                onChange={(e) => setExportData({ ...exportData, bankName: e.target.value })}
                helperText="Leave empty to export all banks"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category (Optional Filter)</InputLabel>
                <Select
                  value={exportData.category}
                  onChange={(e) => setExportData({ ...exportData, category: e.target.value })}
                  label="Category (Optional Filter)"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <MenuItem key={num} value={num}>{num}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Bank Type (Optional Filter)</InputLabel>
                <Select
                  value={exportData.bankType}
                  onChange={(e) => setExportData({ ...exportData, bankType: e.target.value })}
                  label="Bank Type (Optional Filter)"
                >
                  <MenuItem value="">All Bank Types</MenuItem>
                  <MenuItem value="Bob">Bob</MenuItem>
                  <MenuItem value="Non Bob">Non Bob</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date (for DOC)"
                value={exportData.date}
                onChange={(e) => setExportData({ ...exportData, date: e.target.value })}
                helperText="Format: 19th-November-2025"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Value Date (for Excel)"
                value={exportData.valueDate}
                onChange={(e) => setExportData({ ...exportData, valueDate: e.target.value })}
                helperText="Format: DD/MM/YYYY e.g. 17/03/2026"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleExport('txt')}
            disabled={exportLoading}
            startIcon={<DownloadIcon />}
          >
            Export TXT
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleExport('excel')}
            disabled={exportLoading}
            startIcon={<DownloadIcon />}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleExport('doc')}
            disabled={exportLoading}
            startIcon={<DownloadIcon />}
          >
            Export DOC
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Salary Entry Dialog */}
      <Dialog open={salaryAddDialog} onClose={() => setSalaryAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Salary Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Company, Month, Year */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Company</InputLabel>
                  <Select
                    name="company"
                    value={newSalaryData.company}
                    onChange={handleNewSalaryChange}
                    label="Company"
                  >
                    {companies && companies.map((company) => (
                      <MenuItem key={company._id} value={company._id}>
                        {company.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Month</InputLabel>
                  <Select
                    name="month"
                    value={newSalaryData.month}
                    onChange={handleNewSalaryChange}
                    label="Month"
                  >
                    {months.map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Year"
                  name="year"
                  value={newSalaryData.year}
                  onChange={handleNewSalaryChange}
                />
              </Grid>

              {/* Employee Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                  Employee Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Employee Code"
                  name="employeeCode"
                  value={newSalaryData.employeeCode}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  name="firstName"
                  value={newSalaryData.firstName}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Middle Name"
                  name="middleName"
                  value={newSalaryData.middleName}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Last Name"
                  name="lastName"
                  value={newSalaryData.lastName}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Job Title"
                  name="jobTitle"
                  value={newSalaryData.jobTitle}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  name="mobile"
                  value={newSalaryData.mobile}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Alternate Number"
                  name="alternateNumber"
                  value={newSalaryData.alternateNumber}
                  onChange={handleNewSalaryChange}
                />
              </Grid>

              {/* Bank Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                  Bank Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name as Per Bank"
                  name="nameAsPerBank"
                  value={newSalaryData.nameAsPerBank}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  name="bankName"
                  value={newSalaryData.bankName}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Account Number"
                  name="accountNumber"
                  value={newSalaryData.accountNumber}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  name="ifscCode"
                  value={newSalaryData.ifscCode}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    name="accountType"
                    value={newSalaryData.accountType}
                    onChange={handleNewSalaryChange}
                    label="Account Type"
                  >
                    <MenuItem value="SB">Savings (SB)</MenuItem>
                    <MenuItem value="CB">Current (CB)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Bank Type</InputLabel>
                  <Select
                    name="bankType"
                    value={newSalaryData.bankType}
                    onChange={handleNewSalaryChange}
                    label="Bank Type"
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Bob">Bob</MenuItem>
                    <MenuItem value="Non Bob">Non Bob</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="PAN Card"
                  name="panCard"
                  value={newSalaryData.panCard}
                  onChange={(e) => setNewSalaryData({ ...newSalaryData, panCard: e.target.value.toUpperCase() })}
                  inputProps={{ maxLength: 10 }}
                  helperText="10 characters (e.g., ABCDE1234F)"
                />
              </Grid>

              {/* Salary Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                  Salary Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Salary"
                  name="salary"
                  value={newSalaryData.salary}
                  onChange={handleNewSalaryChange}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Attendance (days)"
                  name="attendance"
                  value={newSalaryData.attendance}
                  onChange={handleNewSalaryChange}
                  inputProps={{ min: 0, max: 31, step: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Advance Pay"
                  name="advancePay"
                  value={newSalaryData.advancePay}
                  onChange={handleNewSalaryChange}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="TDS %"
                  name="tds"
                  value={newSalaryData.tds}
                  onChange={handleNewSalaryChange}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Supervisor Name"
                  name="supervisorName"
                  value={newSalaryData.supervisorName}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Process"
                  name="process"
                  value={newSalaryData.process}
                  onChange={handleNewSalaryChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="type"
                    value={newSalaryData.type}
                    onChange={handleNewSalaryChange}
                    label="Type"
                  >
                    <MenuItem value="">None</MenuItem>
                    {salaryTypes.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={newSalaryData.category}
                    onChange={handleNewSalaryChange}
                    label="Category"
                  >
                    <MenuItem value="">None</MenuItem>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryAddDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSalaryAddSubmit}
            variant="contained"
            color="success"
            disabled={salaryCreate.loading}
            startIcon={salaryCreate.loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {salaryCreate.loading ? 'Creating...' : 'Create Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hold Salary Dialog */}
      <Dialog open={salaryHoldDialog} onClose={() => setSalaryHoldDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Hold Salary Record</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedSalary && (
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Employee: {selectedSalary.firstName} {selectedSalary.lastName} ({selectedSalary.employeeCode})
              </Typography>
            )}
            <TextField
              fullWidth
              label="Reason for Hold"
              multiline
              rows={4}
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder="Enter the reason for putting this salary record on hold..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryHoldDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleHoldConfirm}
            disabled={salaryHold.loading || !holdReason.trim()}
          >
            {salaryHold.loading ? 'Holding...' : 'Hold Record'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalaryManagementWithMasters;
